import { useEffect, useMemo, useState } from 'react'

export type SortDirection = 'asc' | 'desc'

export interface SortConfig<T> {
    key: keyof T | string // Allow string/keys
    direction: SortDirection
}

export function useSortedData<T>(
    data: T[],
    defaultKey: keyof T | string,
    defaultDirection: SortDirection = 'desc',
    storageKey?: string,
) {
    const [sortConfig, setSortConfig] = useState<SortConfig<T>>(() => {
        if (storageKey) {
            if (typeof window !== 'undefined') {
                const saved = localStorage.getItem(storageKey)
                if (saved) {
                    try {
                        return JSON.parse(saved)
                    } catch (e) {
                        console.error('Failed to parse saved sort config', e)
                    }
                }
            }
        }
        return { key: defaultKey, direction: defaultDirection }
    })

    useEffect(() => {
        if (storageKey) {
            localStorage.setItem(storageKey, JSON.stringify(sortConfig))
        }
    }, [sortConfig, storageKey])

    const sortedData = useMemo(() => {
        if (!data || data.length === 0) return []

        // OPTIMIZATION: Fast path for Pre-Sorted Data (e.g. from DuckDB)
        const isPreSortedDesc =
            (sortConfig.key === 'time' || sortConfig.key === 'timestamp') && sortConfig.direction === 'desc'

        if (isPreSortedDesc && data.length > 1) {
            const first = (data[0] as any).timestamp
            const second = (data[1] as any).timestamp

            if ((typeof first === 'number' || typeof first === 'string') && first >= second) {
                return data
            }
        }

        // Create a copy to sort
        const sorted = [...data]

        const getValue = (obj: any, path: string | keyof T) => {
            if (typeof path !== 'string') return obj[path]
            return path.split('.').reduce((acc, part) => acc?.[part], obj)
        }

        sorted.sort((a: any, b: any) => {
            const aValue = getValue(a, sortConfig.key)
            const bValue = getValue(b, sortConfig.key)

            if (aValue === bValue) return 0

            if (aValue === null || aValue === undefined) return 1
            if (bValue === null || bValue === undefined) return -1

            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue
            }

            if (typeof aValue === 'string' && typeof bValue === 'string') {
                return sortConfig.direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue)
            }

            if (aValue instanceof Date && bValue instanceof Date) {
                return sortConfig.direction === 'asc'
                    ? aValue.getTime() - bValue.getTime()
                    : bValue.getTime() - aValue.getTime()
            }

            return 0
        })

        return sorted
    }, [data, sortConfig])

    const requestSort = (key: keyof T | string) => {
        setSortConfig((current) => ({
            key,
            direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
        }))
    }

    return { sortedData, sortConfig, requestSort, setSortConfig }
}
